import { useActivityStore } from '@/store/activityStore';
import { cn } from '@/lib/utils';

/**
 * Tier 3 of the progress vocabulary.
 *
 * A 2px gold bar pinned to the very top of the viewport whenever a registered
 * background job is running. It never covers content, never steals focus, and
 * can't interrupt what the user is doing — the mobile-safe answer to "is this
 * thing still working?". Tapping the label reopens the owning surface when the
 * job provided an `onOpen`.
 */
export function GlobalActivityBar() {
	const jobs = Object.values(useActivityStore((s) => s.jobs));
	if (jobs.length === 0) return null;

	const done = jobs.reduce((n, j) => n + j.done, 0);
	const total = jobs.reduce((n, j) => n + j.total, 0);
	const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
	const primary = jobs[0];
	const label =
		jobs.length > 1
			? `${jobs.length} background jobs · ${done} of ${total}`
			: `${primary.label} ${primary.done} of ${primary.total}`;

	return (
		<div
			className="fixed inset-x-0 top-0 z-[100] pointer-events-none"
			role="status"
			aria-live="polite"
		>
			<div className="h-[2px] w-full bg-active-gold/15">
				<div
					className="h-full bg-active-gold transition-[width] duration-300 ease-out"
					style={{ width: `${pct}%` }}
				/>
			</div>
			<div className="flex justify-center">
				<button
					type="button"
					onClick={() => primary.onOpen?.()}
					className={cn(
						'mt-1 rounded-full border border-active-gold/30 bg-active-gold/10 px-2.5 py-0.5',
						'text-[10px] font-medium tabular-nums text-active-gold backdrop-blur-sm',
						primary.onOpen ? 'pointer-events-auto' : 'pointer-events-none',
					)}
				>
					{label}
				</button>
			</div>
		</div>
	);
}
